// PvP real-time battle screen — connects via WebSocket to /api/ws/arena/match/{id}
// Authoritative state lives on the server; client only renders what it receives.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Heart, Droplet, Swords, Hand, Layers, Wifi, WifiOff, Flag, Trophy, X, AlertTriangle, Coins, Sparkles, Skull } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { resolveImageUrl } from "@/utils/imageUrl";
import soundManager from "@/utils/SoundManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLOR = {
  common: "#cbd5e1", uncommon: "#34d399", rare: "#60a5fa", epic: "#a78bfa", legendary: "#fbbf24",
};

function buildWsUrl(matchId, token) {
  // Convert https://… → wss://… and http://… → ws://…
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/arena/match/${matchId}?token=${encodeURIComponent(token)}`;
}

// ------------ Sub-components ------------

function HUD({ player, name, isYourTurn, isOpponent, lang }) {
  const isIt = lang.startsWith("it");
  const hpPct = Math.max(0, Math.min(100, (player.hp / player.max_hp) * 100));
  const hpColor = hpPct > 60 ? "bg-emerald-500" : hpPct > 30 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div
      className={`bg-[#1a1a1d] border ${isYourTurn ? "border-[#cfb53b]" : "border-[#2d3748]"} rounded-lg p-3 ${isYourTurn ? "ring-2 ring-[#cfb53b]/30" : ""}`}
      data-testid={isOpponent ? "arena-hud-opponent" : "arena-hud-self"}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-heading text-sm sm:text-base text-[#cfb53b] truncate">{name}</span>
          {isYourTurn && (
            <span className="text-[10px] bg-[#cfb53b] text-[#0a0a0c] px-1.5 py-0.5 rounded font-heading">
              {isIt ? "Turno" : "Turn"}
            </span>
          )}
        </div>
        <span className="text-[10px] text-[#94a3b8] flex items-center gap-1">
          <Hand size={11} /> {isOpponent ? player.hand_count : player.hand?.length || 0}
          <Layers size={11} className="ml-1" /> {player.deck_count}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <Heart size={12} className="text-red-400 shrink-0" />
        <div className="flex-1 h-2.5 bg-[#0a0a0c] rounded-full overflow-hidden border border-[#2d3748]">
          <div className={`h-full ${hpColor} transition-all`} style={{ width: `${hpPct}%` }} />
        </div>
        <span className="text-xs font-heading text-[#cbd5e1] w-12 text-right">{player.hp}/{player.max_hp}</span>
      </div>
      <div className="flex items-center gap-2">
        <Droplet size={12} className="text-cyan-400 shrink-0" />
        <div className="flex-1 flex gap-0.5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-sm ${
                i < player.mana ? "bg-cyan-400" : i < player.max_mana ? "bg-cyan-900/60" : "bg-[#2d3748]/40"
              }`}
            />
          ))}
        </div>
        <span className="text-xs font-heading text-cyan-300 w-12 text-right">{player.mana}/{player.max_mana}</span>
      </div>
      {player.temp_defense > 0 && (
        <div className="mt-1.5 text-[10px] text-blue-300 flex items-center gap-1">
          <Layers size={10} /> {isIt ? "Armatura:" : "Armor:"} {player.temp_defense}
        </div>
      )}
    </div>
  );
}

function FieldCard({ ally, isOpponent }) {
  const color = RARITY_COLOR[(ally.rarity || "common").toLowerCase()] || RARITY_COLOR.common;
  return (
    <div
      className={`relative rounded-md border-2 ${isOpponent ? "border-red-500/60" : "border-emerald-500/60"} bg-[#1a1a1d] overflow-hidden flex flex-col w-20 sm:w-24`}
      data-testid={`field-card-${ally.instance_id || ally.id}`}
    >
      <div className="aspect-[3/4] bg-[#0a0a0c]">
        {ally.image_url
          ? <img src={resolveImageUrl(ally.image_url, BACKEND_URL)} alt={ally.name} className="w-full h-full object-cover" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center text-[#64748b]"><Swords size={20} /></div>
        }
      </div>
      <div className="p-1 text-center">
        <p className="text-[9px] font-heading truncate" style={{ color }}>{ally.name}</p>
        <p className="text-[10px] flex justify-center gap-1.5 mt-0.5">
          <span className="text-red-300">⚔{ally.attack}</span>
          <span className="text-green-300">♥{ally.hp}</span>
        </p>
      </div>
    </div>
  );
}

function HandCard({ card, canPlay, onPlay, lang }) {
  const isIt = lang.startsWith("it");
  const color = RARITY_COLOR[(card.rarity || "common").toLowerCase()] || RARITY_COLOR.common;
  return (
    <button
      onClick={() => canPlay && onPlay(card.id)}
      disabled={!canPlay}
      data-testid={`hand-card-${card.id}`}
      title={canPlay ? (isIt ? "Gioca carta" : "Play card") : (isIt ? "Mana insufficiente" : "Not enough mana")}
      className={`relative shrink-0 w-24 sm:w-28 rounded-md border-2 transition-all overflow-hidden ${
        canPlay
          ? "border-[#cfb53b]/60 bg-[#1a1a1d] hover:border-[#cfb53b] hover:-translate-y-2 cursor-pointer"
          : "border-[#2d3748] bg-[#1a1a1d]/60 opacity-60 cursor-not-allowed"
      }`}
    >
      <div className="absolute top-0.5 left-0.5 z-10 bg-cyan-500 text-[#0a0a0c] rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-heading">
        {card.mana_cost || 0}
      </div>
      <div className="aspect-[3/4] bg-[#0a0a0c]">
        {card.image_url
          ? <img src={resolveImageUrl(card.image_url, BACKEND_URL)} alt={card.name} className="w-full h-full object-cover" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center text-[#64748b]"><Swords size={22} /></div>
        }
      </div>
      <div className="p-1">
        <p className="text-[9px] font-heading truncate text-center" style={{ color }}>{card.name}</p>
        <p className="text-[9px] flex justify-center gap-1 mt-0.5">
          {card.attack > 0 && <span className="text-red-300">⚔{card.attack}</span>}
          {card.defense > 0 && <span className="text-blue-300">🛡{card.defense}</span>}
          {card.hp > 0 && <span className="text-green-300">♥{card.hp}</span>}
        </p>
      </div>
    </button>
  );
}

// ------------ Main Component ------------

export default function ArenaBattle({ matchId, onExit, opponentName }) {
  const { game } = useGame();
  const { token } = useAuth();
  const [state, setState] = useState(null);
  const [connection, setConnection] = useState("connecting"); // connecting | open | closed | reconnecting
  const [error, setError] = useState(null);
  const [oppDisconnected, setOppDisconnected] = useState(null); // null | {grace_seconds, started_at}
  const [matchEnd, setMatchEnd] = useState(null); // {winner_id, mmr, rewards, score, format}
  const [gameEndOverlay, setGameEndOverlay] = useState(null); // {score, next_game, winner_id} - bo3 inter-game
  const [confirmSurrender, setConfirmSurrender] = useState(false);
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const closedByUsRef = useRef(false);
  const prevTurnRef = useRef(null);  // track turn switches for "your turn" sound
  const lang = (game?.language || "en");
  const isIt = lang.startsWith("it");

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  }, []);

  // Connect WebSocket (with reconnect on transient errors)
  useEffect(() => {
    if (!matchId || !token) return;
    closedByUsRef.current = false;

    const connect = () => {
      setConnection(reconnectAttemptsRef.current > 0 ? "reconnecting" : "connecting");
      const url = buildWsUrl(matchId, token);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setConnection("open");
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "state") {
          setState((prev) => {
            // Sound triggers based on diff
            try {
              if (msg.state) {
                const yourId = msg.state.you;
                const me = msg.state.players[yourId];
                const opp = Object.entries(msg.state.players).find(([k]) => k !== yourId)?.[1];
                // Damage/heal: compare HP
                if (prev?.players) {
                  const prevMe = prev.players[yourId];
                  const prevOpp = Object.entries(prev.players).find(([k]) => k !== yourId)?.[1];
                  if (prevMe && me) {
                    if (me.hp < prevMe.hp) soundManager.playPvpDamage();
                    else if (me.hp > prevMe.hp) soundManager.playPvpHeal();
                  }
                  if (prevOpp && opp && opp.hp < prevOpp.hp) {
                    // we dealt damage — same hit sound
                    soundManager.playPvpDamage();
                  }
                }
                // Turn switch: play "your turn" if it just became my turn
                const newTurnUid = msg.state.current_turn_user_id;
                if (prevTurnRef.current && prevTurnRef.current !== newTurnUid && newTurnUid === yourId) {
                  soundManager.playPvpYourTurn();
                }
                prevTurnRef.current = newTurnUid;
              }
            } catch { /* sound errors are non-fatal */ }
            return msg.state;
          });
        } else if (msg.type === "error") {
          const map = {
            not_your_turn: isIt ? "Non è il tuo turno" : "Not your turn",
            not_enough_mana: isIt ? "Mana insufficiente" : "Not enough mana",
            card_not_in_hand: isIt ? "Carta non disponibile" : "Card not available",
            match_ended: isIt ? "Il match è già terminato" : "Match already ended",
          };
          toast.error(map[msg.code] || msg.code || "Action error");
          setBusy(false);
        } else if (msg.type === "opponent_disconnected") {
          setOppDisconnected({ grace_seconds: msg.grace_seconds || 60, started_at: Date.now() });
          toast.warning(isIt
            ? `Avversario disconnesso. ${msg.grace_seconds || 60}s per riconnettersi.`
            : `Opponent disconnected. ${msg.grace_seconds || 60}s to reconnect.`);
        } else if (msg.type === "opponent_reconnected") {
          setOppDisconnected(null);
          toast.success(isIt ? "Avversario riconnesso" : "Opponent reconnected");
        } else if (msg.type === "game_ended") {
          // BO3 inter-game: show score panel briefly, server will send fresh state
          setGameEndOverlay({
            score: msg.score, next_game: msg.next_game, winner_id: msg.winner_id,
          });
          setTimeout(() => setGameEndOverlay(null), 2500);
        } else if (msg.type === "match_ended") {
          setMatchEnd(msg);
          // Play victory or defeat sound
          const myId = state?.you;
          if (msg.winner_id && myId) {
            if (msg.winner_id === myId) soundManager.playPvpVictory();
            else soundManager.playPvpDefeat();
          }
        } else if (msg.type === "pong") {
          // heartbeat ack
        }
        setBusy(false);
      };
      ws.onclose = (ev) => {
        if (closedByUsRef.current) {
          setConnection("closed");
          return;
        }
        // Auto-reconnect with backoff (up to 5 attempts)
        if (reconnectAttemptsRef.current < 5 && ev.code !== 1008) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(8000, 800 * Math.pow(2, reconnectAttemptsRef.current - 1));
          setConnection("reconnecting");
          setTimeout(connect, delay);
        } else {
          setConnection("closed");
          if (ev.code === 1008) setError(isIt ? "Accesso negato" : "Access denied");
        }
      };
      ws.onerror = () => {
        // close handler will manage reconnect
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      try { wsRef.current?.close(1000); } catch { /* noop */ }
    };
  }, [matchId, token, isIt]);

  // Heartbeat ping every 20s to keep proxy connection alive
  useEffect(() => {
    const id = setInterval(() => send({ type: "ping" }), 20000);
    return () => clearInterval(id);
  }, [send]);

  // Countdown for opponent disconnection grace period
  const [oppGraceLeft, setOppGraceLeft] = useState(0);
  useEffect(() => {
    if (!oppDisconnected) { setOppGraceLeft(0); return; }
    const tick = () => {
      const elapsed = (Date.now() - oppDisconnected.started_at) / 1000;
      const left = Math.max(0, oppDisconnected.grace_seconds - elapsed);
      setOppGraceLeft(Math.ceil(left));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [oppDisconnected]);

  const players = state?.players || {};
  const myId = state?.you;
  const me = myId ? players[myId] : null;
  const oppEntry = useMemo(() => {
    if (!state) return null;
    const entries = Object.entries(players);
    return entries.find(([uid]) => uid !== myId);
  }, [state, players, myId]);
  const opp = oppEntry?.[1];
  const oppId = oppEntry?.[0];
  const isYourTurn = state?.current_turn_user_id === myId && !matchEnd;

  const playCard = (cardId) => {
    if (busy || !isYourTurn) return;
    setBusy(true);
    send({ type: "play_card", card_id: cardId });
  };

  const endTurn = () => {
    if (busy || !isYourTurn) return;
    setBusy(true);
    send({ type: "end_turn" });
  };

  const doSurrender = () => {
    setBusy(true);
    setConfirmSurrender(false);
    send({ type: "surrender" });
  };

  // ---- Loading / error UI ----
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center bg-[#1a1a1d] border border-red-500/40 rounded-lg p-6 max-w-md">
          <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
          <p className="font-heading text-red-300 mb-3">{error}</p>
          <Button variant="outline" onClick={onExit} data-testid="arena-battle-exit">
            <X size={14} className="mr-1" /> {isIt ? "Esci" : "Exit"}
          </Button>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <Loader2 size={32} className="animate-spin text-[#cfb53b] mb-3" />
        <p className="text-[#cbd5e1] font-heading">
          {connection === "connecting" && (isIt ? "Connessione all'arena..." : "Connecting to arena...")}
          {connection === "reconnecting" && (isIt ? "Riconnessione..." : "Reconnecting...")}
          {connection === "closed" && (isIt ? "Connessione persa" : "Connection lost")}
        </p>
        {connection === "closed" && (
          <Button variant="outline" onClick={onExit} className="mt-4" data-testid="arena-battle-exit">
            {isIt ? "Torna alla lobby" : "Back to lobby"}
          </Button>
        )}
      </div>
    );
  }

  // ---- Battle UI ----
  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-4 py-3 space-y-3" data-testid="arena-battle">
      {/* Connection status + BO3 score header */}
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1.5">
          {connection === "open" ? (
            <Wifi size={12} className="text-emerald-400" />
          ) : (
            <WifiOff size={12} className="text-red-400 animate-pulse" />
          )}
          <span className={connection === "open" ? "text-emerald-400" : "text-red-400"}>
            {connection === "open" ? (isIt ? "Connesso" : "Connected") :
              connection === "reconnecting" ? (isIt ? "Riconnessione..." : "Reconnecting...") :
              connection === "closed" ? (isIt ? "Disconnesso" : "Disconnected") :
              (isIt ? "Connessione..." : "Connecting...")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* BO3 score (mostrato solo se backend imposta format/score nello state derivato dalla match_doc; per ora deduco da matchEnd o gameEndOverlay) */}
          <span className="text-[#94a3b8] font-heading">
            {isIt ? "Turno" : "Turn"} {state.turn}
          </span>
        </div>
      </div>

      {oppDisconnected && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md px-3 py-2 text-xs flex items-center gap-2" data-testid="arena-opp-disconnected">
          <WifiOff size={12} className="text-yellow-400" />
          <span className="text-yellow-200 flex-1">
            {isIt
              ? `Avversario disconnesso. Forfeit tra ${oppGraceLeft}s`
              : `Opponent disconnected. Forfeit in ${oppGraceLeft}s`}
          </span>
        </div>
      )}

      {/* Opponent HUD */}
      <HUD
        player={opp || { hp: 0, max_hp: 30, mana: 0, max_mana: 0, hand_count: 0, deck_count: 0, field: [], temp_defense: 0 }}
        name={opponentName || (isIt ? "Avversario" : "Opponent")}
        isYourTurn={state.current_turn_user_id === oppId && !matchEnd}
        isOpponent
        lang={lang}
      />

      {/* Battlefield */}
      <div className="bg-[#0e0e10] border border-[#2d3748] rounded-lg p-2 sm:p-3 space-y-2 min-h-[160px]" data-testid="arena-battlefield">
        <div className="flex justify-center gap-2 flex-wrap min-h-[110px]">
          {(opp?.field || []).length === 0 ? (
            <p className="text-xs text-[#64748b] italic self-center">{isIt ? "Nessun alleato sul campo" : "No allies on field"}</p>
          ) : (
            (opp?.field || []).map((a, i) => <FieldCard key={`opp-${i}`} ally={a} isOpponent />)
          )}
        </div>
        <div className="border-t border-dashed border-[#cfb53b]/20 my-1" />
        <div className="flex justify-center gap-2 flex-wrap min-h-[110px]">
          {(me?.field || []).length === 0 ? (
            <p className="text-xs text-[#64748b] italic self-center">{isIt ? "I tuoi alleati appaiono qui" : "Your allies show here"}</p>
          ) : (
            (me?.field || []).map((a, i) => <FieldCard key={`me-${i}`} ally={a} />)
          )}
        </div>
      </div>

      {/* Self HUD */}
      <HUD
        player={me || { hp: 0, max_hp: 30, mana: 0, max_mana: 0, hand: [], deck_count: 0, field: [], temp_defense: 0 }}
        name={game?.character?.name || (isIt ? "Tu" : "You")}
        isYourTurn={isYourTurn}
        lang={lang}
      />

      {/* Hand */}
      <div className="bg-[#0e0e10] border border-[#2d3748] rounded-lg p-2 sm:p-3" data-testid="arena-hand">
        <p className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-heading mb-2">
          {isIt ? "La tua mano" : "Your hand"} ({me?.hand?.length || 0})
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {(me?.hand || []).length === 0 && (
            <p className="text-xs text-[#64748b] py-4 px-2">{isIt ? "Nessuna carta in mano" : "No cards in hand"}</p>
          )}
          {(me?.hand || []).map((c) => (
            <HandCard
              key={c.id}
              card={c}
              canPlay={isYourTurn && !busy && (c.mana_cost || 0) <= (me?.mana || 0)}
              onPlay={playCard}
              lang={lang}
            />
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => setConfirmSurrender(true)}
          disabled={busy || !!matchEnd}
          data-testid="arena-surrender-btn"
          className="border-red-500/40 text-red-300 hover:bg-red-500/10"
        >
          <Flag size={14} className="mr-1" /> {isIt ? "Arrenditi" : "Surrender"}
        </Button>
        <Button
          onClick={endTurn}
          disabled={!isYourTurn || busy || !!matchEnd}
          data-testid="arena-end-turn-btn"
          className="flex-1 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading h-11"
        >
          {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
          {isYourTurn ? (isIt ? "Termina turno" : "End turn") : (isIt ? "Attendi avversario..." : "Waiting opponent...")}
        </Button>
      </div>

      {/* Surrender confirm */}
      <AlertDialog open={confirmSurrender} onOpenChange={setConfirmSurrender}>
        <AlertDialogContent data-testid="arena-surrender-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{isIt ? "Arrenderti?" : "Surrender?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isIt
                ? "Perderai il match (-15 MMR). Sei sicuro?"
                : "You'll lose the match (-15 MMR). Are you sure?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="arena-surrender-cancel">{isIt ? "Annulla" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doSurrender} data-testid="arena-surrender-confirm" className="bg-red-600 hover:bg-red-700">
              {isIt ? "Arrenditi" : "Surrender"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Match ended overlay */}
      {matchEnd && (
        <div className="fixed inset-0 z-[180] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 arena-match-end-fade" data-testid="arena-match-ended-overlay">
          {matchEnd.winner_id === myId && (
            <div className="arena-confetti-container pointer-events-none">
              {Array.from({ length: 36 }).map((_, i) => (
                <span
                  key={i}
                  className="arena-confetti"
                  style={{
                    left: `${(i * 2.7) % 100}%`,
                    background: ["#cfb53b", "#34d399", "#60a5fa", "#a78bfa", "#fbbf24", "#f87171"][i % 6],
                    animationDelay: `${(i * 0.1) % 2.4}s`,
                    transform: `rotate(${(i * 47) % 360}deg)`,
                  }}
                />
              ))}
            </div>
          )}
          <div className={`bg-[#1a1a1d] border-2 ${matchEnd.winner_id === myId ? "border-[#cfb53b] arena-victory-pulse" : "border-red-500/60 arena-defeat-shake"} rounded-lg p-6 max-w-md w-full text-center space-y-3 shadow-2xl`}>
            {matchEnd.winner_id === myId ? (
              <Trophy size={42} className="mx-auto text-[#fbbf24] arena-trophy-bounce" />
            ) : (
              <Skull size={42} className="mx-auto text-[#94a3b8]" />
            )}
            <h2 className="font-heading text-2xl text-[#cfb53b]">
              {matchEnd.winner_id === myId ? (isIt ? "Vittoria!" : "Victory!") : (isIt ? "Sconfitta" : "Defeat")}
            </h2>

            {/* BO3 final score */}
            {matchEnd.format === "bo3" && matchEnd.score && (
              <p className="text-xs text-[#94a3b8] font-heading">
                {isIt ? "Set finale" : "Final set"}: {matchEnd.score[myId] || 0} – {matchEnd.score[oppId] || 0}
              </p>
            )}

            {/* MMR delta */}
            {matchEnd.mmr && (
              <div className="bg-[#0e0e10] border border-[#2d3748] rounded p-3 text-xs space-y-1">
                <p className="text-[#cbd5e1] flex justify-between">
                  <span>{isIt ? "Vincitore" : "Winner"}:</span>
                  <span className="text-emerald-400 font-heading">+{matchEnd.mmr.winner?.delta || 25} MMR (→{matchEnd.mmr.winner?.new_mmr})</span>
                </p>
                <p className="text-[#cbd5e1] flex justify-between">
                  <span>{isIt ? "Sconfitto" : "Loser"}:</span>
                  <span className="text-red-400 font-heading">{matchEnd.mmr.loser?.delta || -15} MMR (→{matchEnd.mmr.loser?.new_mmr})</span>
                </p>
              </div>
            )}

            {/* Rewards */}
            {matchEnd.rewards && (matchEnd.winner_id === myId
              ? matchEnd.rewards.winner
              : matchEnd.rewards.loser) && (
              <div className="bg-gradient-to-br from-[#cfb53b]/15 to-[#cfb53b]/5 border border-[#cfb53b]/30 rounded p-3" data-testid="arena-rewards-panel">
                <p className="text-[10px] uppercase tracking-wider text-[#cfb53b] font-heading mb-2">
                  {isIt ? "Bottino" : "Spoils"}
                </p>
                <div className="flex justify-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-[#fbbf24]">
                    <Coins size={14} /> +{matchEnd.winner_id === myId ? matchEnd.rewards.winner.gold : matchEnd.rewards.loser.gold}
                  </span>
                  <span className="flex items-center gap-1 text-[#a78bfa]">
                    <Sparkles size={14} /> +{matchEnd.winner_id === myId ? matchEnd.rewards.winner.dust : matchEnd.rewards.loser.dust}
                  </span>
                </div>
                {matchEnd.winner_id === myId && matchEnd.rewards.winner?.card && (
                  <div className="mt-3 pt-3 border-t border-[#cfb53b]/20" data-testid="arena-spoils-card">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-heading mb-1">
                      {isIt ? "🏆 Bottino della Vittoria" : "🏆 Spoils of War"}
                    </p>
                    <p className="text-xs text-[#cbd5e1]">
                      {isIt ? "Hai sottratto:" : "You stole:"}
                    </p>
                    <p className="font-heading text-base mt-1" style={{
                      color: ({rare: "#60a5fa", epic: "#a78bfa", legendary: "#fbbf24"}[matchEnd.rewards.winner.card.rarity] || "#cbd5e1")
                    }}>
                      {matchEnd.rewards.winner.card.name}
                      {matchEnd.rewards.winner.card.foil && " ✦"}
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button onClick={onExit} className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading" data-testid="arena-back-to-lobby">
              {isIt ? "Torna alla lobby" : "Back to lobby"}
            </Button>
          </div>
        </div>
      )}

      {/* BO3 inter-game overlay (between game 1 and 2, or 2 and 3) */}
      {gameEndOverlay && !matchEnd && (
        <div className="fixed inset-0 z-[170] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 arena-match-end-fade" data-testid="arena-game-ended-overlay">
          <div className="bg-[#1a1a1d] border-2 border-[#a78bfa] rounded-lg p-6 max-w-sm w-full text-center space-y-3 shadow-2xl">
            <Trophy size={32} className={`mx-auto ${gameEndOverlay.winner_id === myId ? "text-emerald-400" : "text-red-400"}`} />
            <h3 className="font-heading text-lg text-[#cfb53b]">
              {gameEndOverlay.winner_id === myId
                ? (isIt ? "Hai vinto la partita!" : "Game won!")
                : (isIt ? "Hai perso la partita" : "Game lost")}
            </h3>
            <p className="text-2xl font-heading">
              <span className={(gameEndOverlay.score?.[myId] || 0) > (gameEndOverlay.score?.[oppId] || 0) ? "text-emerald-400" : "text-[#cbd5e1]"}>
                {gameEndOverlay.score?.[myId] || 0}
              </span>
              <span className="text-[#94a3b8] mx-2">–</span>
              <span className={(gameEndOverlay.score?.[oppId] || 0) > (gameEndOverlay.score?.[myId] || 0) ? "text-red-400" : "text-[#cbd5e1]"}>
                {gameEndOverlay.score?.[oppId] || 0}
              </span>
            </p>
            <p className="text-xs text-[#94a3b8]">
              {isIt
                ? `Inizio della partita ${gameEndOverlay.next_game}/3 in 2s...`
                : `Game ${gameEndOverlay.next_game}/3 starting in 2s...`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
